import { Controller } from "react-hook-form";
import Button from "components/button";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useAddDriverAllowanceForm from "./useAddDriverAllowanceForm";

export const addDriverAllowancePageRouteName = "/config/driver-allowance/add";

const AddDriverAllowancePage = () => {
  const {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    showAttachment,
    vehicleModelOptions,
    tripTypeOptions,
    expandedItems,
    toggleExpand,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    handleConfirmCancel,
    formatRupiah,
    t,
    vehicleTypes,
    distanceTypes,
    waitingTimeRates,
    attachmentOptions,
  } = useAddDriverAllowanceForm();

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-6">
        {/* Left Panel - Form */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("section-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {editingIndex !== null
                ? t("section-subtitle-edit")
                : t("section-subtitle-add")}
            </Typography>
          </div>

          <hr className="border-border mb-6" />

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddToList();
            }}
            className="flex flex-col gap-4"
          >
            {/* Vehicle Type */}
            <Controller
              name="vehicleTypeId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-vehicle-type")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={vehicleTypes.map((vt) => ({
                      label: vt.name ?? "",
                      value: vt.id ?? "",
                    }))}
                    value={field.value}
                    placeholder={t("label-vehicle-type")}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-allowance-vehicle-type"
                  />
                </div>
              )}
            />

            {/* Vehicle Attachment (conditional) */}
            {showAttachment && (
              <Controller
                name="vehicleAttachmentIds"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-1.5">
                    <Typography variant="bodyLarge">
                      {t("label-vehicle-attachment")}
                    </Typography>
                    <Select
                      options={attachmentOptions.map((a) => ({
                        label: a.name,
                        value: a.id,
                        description: undefined,
                      }))}
                      value={field.value?.join(",") || ""}
                      placeholder={t("label-vehicle-attachment")}
                      onValueChange={(val) => {
                        const current = field.value || [];
                        const valStr = val as string;
                        if (current.includes(valStr)) {
                          field.onChange(current.filter((v) => v !== valStr));
                        } else {
                          field.onChange([...current, valStr]);
                        }
                      }}
                      data-testid="multiselect-allowance-attachment"
                    />
                    {field.value && field.value.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {field.value.map((attId) => {
                          const att = attachmentOptions.find((a) => a.id === attId);
                          return att ? (
                            <span
                              key={attId}
                              className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-xs text-primary-700"
                            >
                              {att.name}
                              <button
                                type="button"
                                onClick={() =>
                                  field.onChange(field.value.filter((v) => v !== attId))
                                }
                                className="ml-1 text-primary-500 hover:text-primary-700"
                                data-testid={`btn-remove-attachment-${attId}`}
                              >
                                <Icon name="close" size={12} />
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                )}
              />
            )}

            {/* Vehicle Model */}
            <Controller
              name="vehicleModelId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-vehicle-model")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={vehicleModelOptions.map((vm) => ({
                      label: vm.name ?? "",
                      value: vm.id ?? "",
                    }))}
                    value={field.value}
                    placeholder={t("label-vehicle-model")}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-allowance-vehicle-model"
                  />
                </div>
              )}
            />

            {/* Distance Type */}
            <Controller
              name="distanceTypeId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-distance-type")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={distanceTypes.map((dt) => ({
                      label: dt.name ?? "",
                      value: dt.id ?? "",
                    }))}
                    value={field.value}
                    placeholder={t("label-distance-type")}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-allowance-distance-type"
                  />
                </div>
              )}
            />

            {/* Trip Type (auto-populated from vehicle type) */}
            <Controller
              name="tripTypeId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-trip-type")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={tripTypeOptions.map((tt) => ({
                      label: tt.name ?? "",
                      value: tt.id ?? "",
                    }))}
                    value={field.value}
                    placeholder={t("label-trip-type")}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    disabled={tripTypeOptions.length === 0}
                    data-testid="dropdown-allowance-trip-type"
                  />
                </div>
              )}
            />

            {/* Driver Basic Rate */}
            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-driver-basic-rate")}{" "}
                <span className="text-error-500">*</span>
              </Typography>
              <div className="flex items-center gap-2">
                <span className="rounded-lg bg-primary-50 px-3 py-2 text-sm font-medium text-primary-700 whitespace-nowrap">
                  Rp.
                </span>
                <Input
                  enableNumberFormat
                  type="number"
                  placeholder="0"
                  {...register("driverBasicRate")}
                  error={errors.driverBasicRate}
                  data-testid="input-allowance-driver-basic-rate"
                />
              </div>
              <Typography variant="body" tone="muted" className="text-xs">
                {t("helper-driver-basic-rate")}
              </Typography>
            </div>

            {/* Driver Incentive Rate */}
            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-driver-incentive-rate")}{" "}
                <span className="text-error-500">*</span>
              </Typography>
              <div className="flex items-center gap-2">
                <span className="rounded-lg bg-primary-50 px-3 py-2 text-sm font-medium text-primary-700 whitespace-nowrap">
                  Rp.
                </span>
                <Input
                  enableNumberFormat
                  type="number"
                  placeholder="0"
                  {...register("driverIncentiveRate")}
                  error={errors.driverIncentiveRate}
                  data-testid="input-allowance-driver-incentive-rate"
                />
              </div>
              <Typography variant="body" tone="muted" className="text-xs">
                {t("helper-driver-incentive-rate")}
              </Typography>
            </div>

            {/* Waiting Time Rate */}
            <Controller
              name="waitingTimeRateId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-waiting-time-rate")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={waitingTimeRates.map((wtr) => ({
                      label: wtr.name ?? "",
                      value: wtr.id ?? "",
                    }))}
                    value={field.value}
                    placeholder={t("label-waiting-time-rate")}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-allowance-waiting-time-rate"
                  />
                  <Typography variant="body" tone="muted" className="text-xs">
                    {t("helper-waiting-time-rate")}
                  </Typography>
                </div>
              )}
            />

            {/* Status */}
            <Controller
              name="status"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-status")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={[
                      { label: t("status-active"), value: "active" },
                      { label: t("status-inactive"), value: "inactive" },
                    ]}
                    value={field.value}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-allowance-status"
                  />
                </div>
              )}
            />

            {/* Add / Edit buttons */}
            {editingIndex !== null ? (
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  color="tertiary"
                  size="medium"
                  isFullSize
                  onClick={handleDiscardChanges}
                  data-testid="btn-discard-changes-allowance"
                >
                  {t("btn-discard-changes")}
                </Button>
                <Button
                  type="submit"
                  variant="fill"
                  color="primary"
                  size="medium"
                  isFullSize
                  data-testid="btn-save-changes-allowance"
                >
                  {t("btn-save-changes")}
                </Button>
              </div>
            ) : (
              <Button
                type="submit"
                variant="fill"
                color="primary"
                size="medium"
                isFullSize
                data-testid="btn-add-more-allowance"
              >
                {t("btn-add-to-list")}
              </Button>
            )}
          </form>
        </section>

        {/* Right Panel - Items List */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("list-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {t("list-subtitle")}
            </Typography>
          </div>

          {fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon
                name="document-text"
                size={48}
                className="text-greyScale-60 mb-4"
              />
              <Typography variant="body" tone="muted">
                {t("no-data-title")}
              </Typography>
              <Typography variant="body" tone="muted">
                {t("no-data-subtitle")}
              </Typography>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-[600px] overflow-y-auto">
              {fields.map((item, index) => {
                const isExpanded = expandedItems[index] ?? false;
                return (
                  <div
                    key={item.id}
                    className={`rounded-xl border p-4 ${
                      editingIndex === index
                        ? "border-primary-500 bg-primary-50"
                        : "border-border"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex flex-col gap-1 flex-1">
                        <Typography variant="bodyLarge" className="font-semibold">
                          {item.vehicleTypeName}
                        </Typography>

                        {/* Always visible fields */}
                        <div className="flex items-center gap-2">
                          <Typography variant="body">{t("label-vehicle-model")}</Typography>
                          <Typography variant="body">:</Typography>
                          <Typography variant="body" tone="muted">{item.vehicleModelName}</Typography>
                        </div>
                        <div className="flex items-center gap-2">
                          <Typography variant="body">{t("label-distance-type")}</Typography>
                          <Typography variant="body">:</Typography>
                          <div className="text-sm text-greyScale-40">
                            <span>{item.distanceTypeName}</span>
                            {item.distanceTypeRange && (
                              <span className="text-xs ml-1">({item.distanceTypeRange})</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Typography variant="body">{t("label-trip-type")}</Typography>
                          <Typography variant="body">:</Typography>
                          <Typography variant="body" tone="muted">{item.tripTypeName}</Typography>
                        </div>

                        {/* Expandable fields */}
                        {isExpanded && (
                          <>
                            <div className="flex items-center gap-2">
                              <Typography variant="body">{t("label-driver-basic-rate")}</Typography>
                              <Typography variant="body">:</Typography>
                              <Typography variant="body" tone="muted">
                                {formatRupiah(item.driverBasicRate)}
                              </Typography>
                            </div>
                            <div className="flex items-center gap-2">
                              <Typography variant="body">{t("label-driver-incentive-rate")}</Typography>
                              <Typography variant="body">:</Typography>
                              <Typography variant="body" tone="muted">
                                {formatRupiah(item.driverIncentiveRate)}
                              </Typography>
                            </div>
                            <div className="flex items-center gap-2">
                              <Typography variant="body">{t("label-waiting-time-rate")}</Typography>
                              <Typography variant="body">:</Typography>
                              <Typography variant="body" tone="muted">{item.waitingTimeRateName}</Typography>
                            </div>
                            {item.vehicleAttachmentNames.length > 0 && (
                              <div className="flex items-center gap-2">
                                <Typography variant="body">{t("label-vehicle-attachment")}</Typography>
                                <Typography variant="body">:</Typography>
                                <Typography variant="body" tone="muted">
                                  {item.vehicleAttachmentNames.join(", ")}
                                </Typography>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Typography variant="body">{t("label-status")}</Typography>
                              <Typography variant="body">:</Typography>
                              <Typography variant="body" tone="muted" className="capitalize">
                                {item.status}
                              </Typography>
                            </div>
                          </>
                        )}

                        {/* Show more / Show less */}
                        <button
                          type="button"
                          onClick={() => toggleExpand(index)}
                          className="text-primary-500 text-sm text-left mt-1 cursor-pointer hover:underline"
                          data-testid={`btn-toggle-expand-allowance-${index}`}
                        >
                          {isExpanded ? t("show-less") : t("show-more")} &#8593;
                        </button>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-3 ml-4">
                        <button
                          type="button"
                          onClick={() => handleEditItem(index)}
                          className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer border ${
                            editingIndex === index
                              ? "bg-primary-500 text-white border-primary-500"
                              : "hover:bg-greyScale-90 text-primary-500 border-primary-500"
                          }`}
                          data-testid={`btn-edit-row-allowance-${index}`}
                        >
                          <Icon name="edit-2" size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteItem(index)}
                          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-error-50 text-error-500 transition-colors cursor-pointer border border-error-500"
                          data-testid={`btn-delete-row-allowance-${index}`}
                        >
                          <Icon name="trash" size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={handleCancel}
          data-testid="btn-cancel-allowance"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={() => setShowSaveModal(true)}
          disabled={fields.length === 0}
          data-testid="btn-save-allowance"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleSave}
        onCancel={() => setShowSaveModal(false)}
        testId="btn-save-confirmation-modal-allowance"
      />

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-cancel-title")}
        message={t("modal-cancel-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        testId="btn-cancel-confirmation-modal-allowance"
      />
    </div>
  );
};

export default AddDriverAllowancePage;
